import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';
import ImageUpload from './ImageUpload';

jest.mock('components/styleguide/icon/Icon', () => ({
  __esModule: true,
  default: ({ name, ...props }: any) => <span data-icon={name} {...props} />,
}));

jest.mock('components/styleguide/button/Button', () => ({
  __esModule: true,
  default: ({ children, onClick, 'aria-label': ariaLabel, iconLeft }: any) => (
    <button onClick={onClick} aria-label={ariaLabel} data-icon={iconLeft}>
      {children}
    </button>
  ),
}));

const createFile = (name = 'photo.png', size = 1024, type = 'image/png') => {
  const file = new File(['x'.repeat(size)], name, { type });
  Object.defineProperty(file, 'size', { value: size });
  return file;
};

describe('ImageUpload', () => {
  beforeEach(() => {
    jest.clearAllMocks();
  });

  it('renders placeholder when no image', () => {
    const { container } = render(<ImageUpload />);
    expect(container.querySelector('[data-icon="camera"]')).toBeInTheDocument();
  });

  it('renders image when imageUrl is provided', () => {
    render(<ImageUpload imageUrl="/logo.png" />);
    expect(screen.getByAltText('Uploaded image')).toHaveAttribute('src', '/logo.png');
  });

  it('supports custom alt text', () => {
    render(<ImageUpload imageUrl="/logo.png" alt="Company logo" />);
    expect(screen.getByAltText('Company logo')).toBeInTheDocument();
  });

  it('renders upload button when not readOnly', () => {
    render(<ImageUpload />);
    expect(screen.getByLabelText('Upload image')).toBeInTheDocument();
  });

  it('renders replace button when image exists', () => {
    render(<ImageUpload imageUrl="/logo.png" />);
    expect(screen.getByLabelText('Replace image')).toBeInTheDocument();
  });

  it('does not render button when readOnly', () => {
    render(<ImageUpload readOnly imageUrl="/logo.png" />);
    expect(screen.queryByRole('button')).not.toBeInTheDocument();
  });

  it('renders constraints text', () => {
    render(<ImageUpload />);
    expect(screen.getByText(/Min. dimensions/)).toBeInTheDocument();
    expect(screen.getByText(/JPG or PNG only/)).toBeInTheDocument();
  });

  describe('file validation', () => {
    it('shows error for invalid file type', () => {
      render(<ImageUpload />);
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;
      const file = createFile('doc.pdf', 1024, 'application/pdf');
      fireEvent.change(input, { target: { files: [file] } });
      expect(screen.getByRole('alert')).toHaveTextContent('Only JPG or PNG files are allowed');
    });

    it('shows error for oversized file', () => {
      render(<ImageUpload />);
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;
      const file = createFile('big.png', 6 * 1024 * 1024, 'image/png');
      fireEvent.change(input, { target: { files: [file] } });
      expect(screen.getByRole('alert')).toHaveTextContent('File size must be less than 5 MB');
    });

    it('calls onError callback on validation failure', () => {
      const onError = jest.fn();
      render(<ImageUpload onError={onError} />);
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;
      const file = createFile('doc.pdf', 1024, 'application/pdf');
      fireEvent.change(input, { target: { files: [file] } });
      expect(onError).toHaveBeenCalledWith('Only JPG or PNG files are allowed');
    });
  });

  describe('accessibility', () => {
    it('hides placeholder from screen readers', () => {
      const { container } = render(<ImageUpload />);
      const placeholder = container.querySelector('[aria-hidden="true"]');
      expect(placeholder).toBeInTheDocument();
    });

    it('has aria-label on the file input', () => {
      render(<ImageUpload />);
      const input = document.querySelector('input[type="file"]');
      expect(input).toHaveAttribute('aria-label', 'Choose image to upload');
    });

    it('renders error with role="alert"', () => {
      render(<ImageUpload />);
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;
      const file = createFile('doc.pdf', 1024, 'application/pdf');
      fireEvent.change(input, { target: { files: [file] } });
      expect(screen.getByRole('alert')).toBeInTheDocument();
    });

    it('does not render alert when no error', () => {
      render(<ImageUpload />);
      expect(screen.queryByRole('alert')).not.toBeInTheDocument();
    });

    it('has aria-label on upload button', () => {
      render(<ImageUpload />);
      expect(screen.getByLabelText('Upload image')).toBeInTheDocument();
    });

    it('image has configurable alt text', () => {
      render(<ImageUpload imageUrl="/logo.png" alt="My company logo" />);
      expect(screen.getByAltText('My company logo')).toBeInTheDocument();
    });
  });

  it('sets name attribute on file input', () => {
    render(<ImageUpload name="company[logo]" />);
    const input = document.querySelector('input[type="file"]');
    expect(input).toHaveAttribute('name', 'company[logo]');
  });

  it('sets accept attribute on file input', () => {
    render(<ImageUpload />);
    const input = document.querySelector('input[type="file"]');
    expect(input).toHaveAttribute('accept', 'image/jpeg,image/png');
  });
});
